/**
 * Session + memory cache for lookup APIs (statuses, countries, states, sources, brands,
 * business categories, equipment types, manufacturers, leases, merchant service providers).
 * Soft reloads within TTL skip network for unchanged reference data.
 * Status entries are keyed by auth token + model so impersonation cannot reuse another user's list.
 */

import { hashToken } from './authSessionCache';

const CACHE_KEY = 'revivepay_lookup_cache';
export const LOOKUP_SESSION_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

const memory = {
  countries: null,
  countriesAt: 0,
  usCountryId: null,
  usCountryIdAt: 0,
  sources: null,
  sourcesAt: 0,
  brands: null,
  brandsAt: 0,
  businessCategories: null,
  businessCategoriesAt: 0,
  equipmentTypes: null,
  equipmentTypesAt: 0,
  manufacturers: null,
  manufacturersAt: 0,
  leases: null,
  leasesAt: 0,
  merchantServiceProviders: null,
  merchantServiceProvidersAt: 0,
  /** @type {Record<string, { data: unknown, at: number }>} */
  statesByCountryId: {},
  /** @type {Record<string, { data: unknown, at: number }>} */
  statusesByKey: {},
};

/** @type {Map<string, Promise<unknown>>} */
const statusInflight = new Map();

function currentTokenKey() {
  try {
    return hashToken(localStorage.getItem('token') || '') || 'anon';
  } catch {
    return 'anon';
  }
}

export function getStatusModelFromParams(params = {}) {
  return String(
    params['conditions[model]'] ??
      params.model ??
      (params.conditions && params.conditions.model) ??
      'all'
  );
}

export function getStatusCacheKey(params = {}) {
  return `${currentTokenKey()}::${getStatusModelFromParams(params)}`;
}

function isFresh(at, ttlMs = LOOKUP_SESSION_CACHE_TTL_MS) {
  const age = Date.now() - Number(at || 0);
  return Number.isFinite(age) && age >= 0 && age <= ttlMs;
}

function readSession() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeSession(patch) {
  try {
    const prev = readSession() || {};
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...prev, ...patch }));
  } catch {
    // ignore
  }
}

export function clearLookupSessionCache() {
  memory.countries = null;
  memory.countriesAt = 0;
  memory.usCountryId = null;
  memory.usCountryIdAt = 0;
  memory.sources = null;
  memory.sourcesAt = 0;
  memory.brands = null;
  memory.brandsAt = 0;
  memory.businessCategories = null;
  memory.businessCategoriesAt = 0;
  memory.equipmentTypes = null;
  memory.equipmentTypesAt = 0;
  memory.manufacturers = null;
  memory.manufacturersAt = 0;
  memory.leases = null;
  memory.leasesAt = 0;
  memory.merchantServiceProviders = null;
  memory.merchantServiceProvidersAt = 0;
  memory.statesByCountryId = {};
  memory.statusesByKey = {};
  statusInflight.clear();
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/** Drop cached statuses for one model (or all models if omitted). */
export function clearStatusesCache(model) {
  statusInflight.clear();
  if (!model) {
    memory.statusesByKey = {};
    writeSession({ statusesByKey: {} });
    return;
  }
  const modelKey = String(model);
  Object.keys(memory.statusesByKey).forEach((key) => {
    if (key === modelKey || key.endsWith(`::${modelKey}`)) {
      delete memory.statusesByKey[key];
    }
  });
  const stored = readSession() || {};
  const next = { ...(stored.statusesByKey || {}) };
  Object.keys(next).forEach((key) => {
    if (key === modelKey || key.endsWith(`::${modelKey}`)) {
      delete next[key];
    }
  });
  writeSession({ statusesByKey: next });
}

export function getStatusInflight(params = {}) {
  return statusInflight.get(getStatusCacheKey(params)) || null;
}

export function setStatusInflight(params = {}, promise) {
  const key = getStatusCacheKey(params);
  statusInflight.set(key, promise);
  const clear = () => {
    if (statusInflight.get(key) === promise) statusInflight.delete(key);
  };
  Promise.resolve(promise).then(clear, clear);
  return promise;
}

function hydrateMemoryFromSession() {
  const stored = readSession();
  if (!stored) return;
  if (stored.countries && isFresh(stored.countriesAt) && !memory.countries) {
    memory.countries = stored.countries;
    memory.countriesAt = stored.countriesAt;
  }
  if (stored.usCountryId != null && isFresh(stored.usCountryIdAt) && memory.usCountryId == null) {
    memory.usCountryId = stored.usCountryId;
    memory.usCountryIdAt = stored.usCountryIdAt;
  }
  if (stored.sources && isFresh(stored.sourcesAt) && !memory.sources) {
    memory.sources = stored.sources;
    memory.sourcesAt = stored.sourcesAt;
  }
  if (stored.brands && isFresh(stored.brandsAt) && !memory.brands) {
    memory.brands = stored.brands;
    memory.brandsAt = stored.brandsAt;
  }
  if (stored.businessCategories && isFresh(stored.businessCategoriesAt) && !memory.businessCategories) {
    memory.businessCategories = stored.businessCategories;
    memory.businessCategoriesAt = stored.businessCategoriesAt;
  }
  if (stored.equipmentTypes && isFresh(stored.equipmentTypesAt) && !memory.equipmentTypes) {
    memory.equipmentTypes = stored.equipmentTypes;
    memory.equipmentTypesAt = stored.equipmentTypesAt;
  }
  if (stored.manufacturers && isFresh(stored.manufacturersAt) && !memory.manufacturers) {
    memory.manufacturers = stored.manufacturers;
    memory.manufacturersAt = stored.manufacturersAt;
  }
  if (stored.leases && isFresh(stored.leasesAt) && !memory.leases) {
    memory.leases = stored.leases;
    memory.leasesAt = stored.leasesAt;
  }
  if (
    stored.merchantServiceProviders &&
    isFresh(stored.merchantServiceProvidersAt) &&
    !memory.merchantServiceProviders
  ) {
    memory.merchantServiceProviders = stored.merchantServiceProviders;
    memory.merchantServiceProvidersAt = stored.merchantServiceProvidersAt;
  }
  if (stored.statesByCountryId && typeof stored.statesByCountryId === 'object') {
    Object.entries(stored.statesByCountryId).forEach(([id, entry]) => {
      if (entry?.data && isFresh(entry.at) && !memory.statesByCountryId[id]) {
        memory.statesByCountryId[id] = entry;
      }
    });
  }
  if (stored.statusesByKey && typeof stored.statusesByKey === 'object') {
    Object.entries(stored.statusesByKey).forEach(([key, entry]) => {
      if (entry?.data != null && isFresh(entry.at) && !memory.statusesByKey[key]) {
        memory.statusesByKey[key] = entry;
      }
    });
  }
}

export function getCachedStatusesPayload(params = {}) {
  hydrateMemoryFromSession();
  const key = getStatusCacheKey(params);
  const entry = memory.statusesByKey[key];
  if (entry?.data != null && isFresh(entry.at)) return entry.data;
  return null;
}

export function setCachedStatusesPayload(params = {}, data) {
  const key = getStatusCacheKey(params);
  const entry = { data, at: Date.now() };
  memory.statusesByKey[key] = entry;
  const stored = readSession() || {};
  writeSession({
    statusesByKey: {
      ...(stored.statusesByKey || {}),
      [key]: entry,
    },
  });
}

export function getCachedCountries() {
  hydrateMemoryFromSession();
  if (memory.countries && isFresh(memory.countriesAt)) return memory.countries;
  return null;
}

export function setCachedCountries(list) {
  const data = Array.isArray(list) ? list : [];
  memory.countries = data;
  memory.countriesAt = Date.now();
  writeSession({ countries: data, countriesAt: memory.countriesAt });
}

export function getCachedUsCountryId() {
  hydrateMemoryFromSession();
  if (memory.usCountryId != null && isFresh(memory.usCountryIdAt)) {
    return memory.usCountryId;
  }
  return null;
}

export function setCachedUsCountryId(id) {
  if (id == null) return;
  memory.usCountryId = id;
  memory.usCountryIdAt = Date.now();
  writeSession({ usCountryId: id, usCountryIdAt: memory.usCountryIdAt });
}

export function getCachedSourcesPayload() {
  hydrateMemoryFromSession();
  if (memory.sources != null && isFresh(memory.sourcesAt)) return memory.sources;
  return null;
}

export function setCachedSourcesPayload(data) {
  memory.sources = data;
  memory.sourcesAt = Date.now();
  writeSession({ sources: data, sourcesAt: memory.sourcesAt });
}

export function getCachedBrandsPayload() {
  hydrateMemoryFromSession();
  if (memory.brands != null && isFresh(memory.brandsAt)) return memory.brands;
  return null;
}

export function setCachedBrandsPayload(data) {
  memory.brands = data;
  memory.brandsAt = Date.now();
  writeSession({ brands: data, brandsAt: memory.brandsAt });
}

export function getCachedBusinessCategoriesPayload() {
  hydrateMemoryFromSession();
  if (memory.businessCategories != null && isFresh(memory.businessCategoriesAt)) {
    return memory.businessCategories;
  }
  return null;
}

export function setCachedBusinessCategoriesPayload(data) {
  memory.businessCategories = data;
  memory.businessCategoriesAt = Date.now();
  writeSession({
    businessCategories: data,
    businessCategoriesAt: memory.businessCategoriesAt,
  });
}

function makeSimplePayloadCache(key, atKey) {
  return {
    get() {
      hydrateMemoryFromSession();
      if (memory[key] != null && isFresh(memory[atKey])) return memory[key];
      return null;
    },
    set(data) {
      memory[key] = data;
      memory[atKey] = Date.now();
      writeSession({ [key]: data, [atKey]: memory[atKey] });
    },
  };
}

const equipmentTypesCache = makeSimplePayloadCache('equipmentTypes', 'equipmentTypesAt');
const manufacturersCache = makeSimplePayloadCache('manufacturers', 'manufacturersAt');
const leasesCache = makeSimplePayloadCache('leases', 'leasesAt');
const merchantServiceProvidersCache = makeSimplePayloadCache(
  'merchantServiceProviders',
  'merchantServiceProvidersAt'
);

export const getCachedEquipmentTypesPayload = () => equipmentTypesCache.get();
export const setCachedEquipmentTypesPayload = (data) => equipmentTypesCache.set(data);
export const getCachedManufacturersPayload = () => manufacturersCache.get();
export const setCachedManufacturersPayload = (data) => manufacturersCache.set(data);
export const getCachedLeasesPayload = () => leasesCache.get();
export const setCachedLeasesPayload = (data) => leasesCache.set(data);
export const getCachedMerchantServiceProvidersPayload = () => merchantServiceProvidersCache.get();
export const setCachedMerchantServiceProvidersPayload = (data) =>
  merchantServiceProvidersCache.set(data);

export function getCachedStates(countryId) {
  if (!countryId) return null;
  hydrateMemoryFromSession();
  const key = String(countryId);
  const entry = memory.statesByCountryId[key];
  if (entry?.data && isFresh(entry.at)) return entry.data;
  return null;
}

export function setCachedStates(countryId, list) {
  if (!countryId) return;
  const key = String(countryId);
  const entry = { data: Array.isArray(list) ? list : list ?? [], at: Date.now() };
  memory.statesByCountryId[key] = entry;
  const stored = readSession() || {};
  writeSession({
    statesByCountryId: {
      ...(stored.statesByCountryId || {}),
      [key]: entry,
    },
  });
}

/** Find United States id from a countries list. */
export function findUnitedStatesId(countries) {
  const list = Array.isArray(countries) ? countries : [];
  const us = list.find(
    (c) => c?.name === 'United States' || c?.name === 'United States of America'
  );
  return us?.id ?? null;
}
